import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { fetchPricingSettings, bulkUpdatePricingSettings } from "./DataService";

const GROUP_LABELS = {
  base: "Tarif de base",
  poids: "Suppléments poids",
  temps: "Temps",
  livraison: "Type de livraison",
  manutention: "Manutention",
  hors_compte: "Tarifs hors compte sans équivalent en compte",
};

// Le suffixe qui relie un tarif a son jumeau hors compte, cote API comme ici.
const SUFFIXE_HORS_COMPTE = "_hors_compte";

// Les groupes porteurs de tarifs, dans l'ordre d'affichage. Le groupe
// hors_compte n'y figure pas : ses valeurs sont affichees en seconde colonne
// face a leur jumeau, et non dans un bloc separe.
const GROUP_ORDER = ["base", "poids", "temps", "livraison", "manutention"];

function TarifsTransport() {
  const [settings, setSettings] = useState(null); // { poids: [...], temps: [...], livraison: [...] }
  const [values, setValues] = useState({}); // { [id]: value }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPricingSettings()
      .then((data) => {
        setSettings(data.settings);
        const initial = {};
        Object.values(data.settings).forEach((group) => {
          group.forEach((s) => {
            initial[s.id] = String(s.value);
          });
        });
        setValues(initial);
        setLoading(false);
      })
      .catch((err) => {
        setError("Impossible de charger les tarifs.");
        setLoading(false);
      });
  }, []);

  const handleChange = (id, val) => {
    setValues((prev) => ({ ...prev, [id]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);
    setError(null);

    try {
      const payload = Object.entries(values).map(([id, value]) => ({
        id: parseInt(id, 10),
        value: parseFloat(value),
      }));
      await bulkUpdatePricingSettings(payload);
      setSuccess(true);
    } catch (err) {
      setError("Erreur lors de l'enregistrement des tarifs.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  // Les tarifs hors compte, indexes par la cle de leur jumeau, pour les poser
  // face a lui. On note ceux qui trouvent un jumeau : ceux qui n'en trouvent
  // pas doivent rester editables plus bas, sinon un tarif deviendrait
  // invisible et personne ne saurait qu'il s'applique encore.
  const horsCompte = settings?.hors_compte ?? [];
  const jumeauxParCle = {};
  horsCompte.forEach((s) => {
    if (s.key.endsWith(SUFFIXE_HORS_COMPTE)) {
      jumeauxParCle[s.key.slice(0, -SUFFIXE_HORS_COMPTE.length)] = s;
    }
  });

  const jumeauxUtilises = new Set();
  GROUP_ORDER.forEach((group) => {
    (settings?.[group] ?? []).forEach((s) => {
      if (jumeauxParCle[s.key]) jumeauxUtilises.add(jumeauxParCle[s.key].id);
    });
  });
  const orphelins = horsCompte.filter((s) => !jumeauxUtilises.has(s.id));

  const unite = (s) =>
    s.key === "seuil_minutes" ? "min" : s.key.includes("seuil_poids") ? "kg" : "€";

  const champ = (s) => (
    <Form.Control
      type="number"
      step="0.01"
      min="0"
      value={values[s.id] ?? ""}
      onChange={(e) => handleChange(s.id, e.target.value)}
      required
    />
  );

  return (
    <Container className="mt-4">
      <Row className="mb-3">
        <Col>
          <h2>Tarifs Transport</h2>
          <p className="text-muted mb-1">
            Modifiez les tarifs et enregistrez. Les changements s'appliquent immédiatement.
          </p>
          <p className="text-muted small mb-0">
            La colonne <strong>Hors compte</strong> s'applique aux clients sans paiement en
            compte, quel que soit leur type. Laissée vide, elle n'existe pas : le tarif en
            compte est alors appliqué à tout le monde.
          </p>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">Tarifs mis à jour avec succès.</Alert>}

      <Form onSubmit={handleSubmit}>
        {GROUP_ORDER.map((group) => {
          const items = settings?.[group];
          if (!items || items.length === 0) return null;

          return (
            <Card className="mb-4" key={group}>
              <Card.Header>
                <strong>{GROUP_LABELS[group] || group}</strong>
              </Card.Header>
              <Card.Body>
                <Row className="mb-2 text-muted small fw-bold">
                  <Col sm={4} />
                  <Col sm={3}>En compte</Col>
                  <Col sm={3}>Hors compte</Col>
                  <Col sm={2} />
                </Row>

                {items.map((s) => {
                  const jumeau = jumeauxParCle[s.key];

                  return (
                    <Form.Group as={Row} className="mb-2 align-items-center" key={s.id}>
                      <Form.Label column sm={4}>
                        {s.label}
                      </Form.Label>
                      <Col sm={3}>{champ(s)}</Col>
                      <Col sm={3}>
                        {jumeau ? (
                          champ(jumeau)
                        ) : (
                          // Un seuil n'est pas un tarif : il ne se dedouble pas.
                          <span className="text-muted small">non dédoublé</span>
                        )}
                      </Col>
                      <Col sm={2} className="text-muted">
                        {unite(s)}
                      </Col>
                    </Form.Group>
                  );
                })}
              </Card.Body>
            </Card>
          );
        })}

        {orphelins.length > 0 && (
          <Card className="mb-4 border-warning">
            <Card.Header>
              <strong>{GROUP_LABELS.hors_compte}</strong>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small">
                Ces tarifs hors compte ne correspondent à aucun tarif en compte connu. Ils
                s'appliquent quand même.
              </p>
              {orphelins.map((s) => (
                <Form.Group as={Row} className="mb-2 align-items-center" key={s.id}>
                  <Form.Label column sm={4}>
                    {s.label}
                  </Form.Label>
                  <Col sm={3} />
                  <Col sm={3}>{champ(s)}</Col>
                  <Col sm={2} className="text-muted">
                    {unite(s)}
                  </Col>
                </Form.Group>
              ))}
            </Card.Body>
          </Card>
        )}

        <div className="d-flex justify-content-end mb-5">
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Enregistrer
          </Button>
        </div>
      </Form>
    </Container>
  );
}

export default TarifsTransport;
